import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, CalendarClock } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { submitContact } from "../api";
import { SOCIAL_ICONS } from "../components/Footer";
import { Reveal } from "../hooks/useReveal";

const initialForm = {
  name: "",
  email: "",
  company: "",
  message: "",
  referral: "",
};

export default function Contact() {
  const { content } = useContent();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm(initialForm);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#0d0d0d] border border-white/80 rounded-[4px] px-4 py-3.5 font-comfortaa text-sm text-white outline-none focus:border-white focus:ring-1 focus:ring-white/50";

  return (
    <div className="bg-[#141414] texture-dark">
      {/* Page hero */}
      <section className="bg-[#d8d8d8] slant-bottom-right relative z-10" data-testid="contact-hero">
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-28 md:pb-40 text-center">
          <p className="kicker text-neutral-500 mb-5">Get In Touch</p>
          <h1 className="font-serif-display text-neutral-950 leading-none"
            style={{ fontSize: "clamp(52px, 10vw, 140px)" }} data-testid="contact-heading">
            {content.contact.heading}
          </h1>
          <hr className="border-neutral-900/70 max-w-3xl mx-auto mt-2" />
          <h3 className="font-comfortaa text-neutral-900 leading-relaxed mt-8 max-w-3xl mx-auto font-normal"
            style={{ fontSize: "clamp(17px, 2vw, 26px)" }}>
            {content.contact.intro}
          </h3>
        </div>
      </section>

      {/* Form — dark */}
      <section className="texture-dark pt-16 md:pt-20 pb-24" data-testid="contact-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-comfortaa text-white text-center text-lg mb-10">
              {content.contact.formNote}
            </p>
          </Reveal>

          {submitted ? (
            <Reveal className="text-center py-12" data-testid="contact-success">
              <p className="font-comfortaa text-white text-xl" data-testid="contact-success-message">
                Thank you for reaching out.
              </p>
              <p className="font-comfortaa text-white/70 text-sm mt-3">
                We'll get back to you within 24 hours.
              </p>
              <button
                className="pill-button mt-8"
                onClick={() => setSubmitted(false)}
                data-testid="contact-send-another"
              >
                Send another message
              </button>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="font-comfortaa text-sm text-white block mb-2">
                    Name <span className="text-white/50 text-xs">(required)</span>
                  </label>
                  <input className={inputCls} value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="font-comfortaa text-sm text-white block mb-2">
                    Email <span className="text-white/50 text-xs">(required)</span>
                  </label>
                  <input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="font-comfortaa text-sm text-white block mb-2">Company / Brand</label>
                  <input className={inputCls} value={form.company} onChange={set("company")} data-testid="contact-company-input" />
                </div>
                <div>
                  <label className="font-comfortaa text-sm text-white block mb-2">
                    How can we help? <span className="text-white/50 text-xs">(required)</span>
                  </label>
                  <textarea rows={5} className={inputCls} value={form.message} onChange={set("message")} data-testid="contact-message-input" />
                </div>
                <div>
                  <label className="font-comfortaa text-sm text-white block mb-2">How did you hear about us?</label>
                  <input className={inputCls} value={form.referral} onChange={set("referral")} data-testid="contact-referral-input" />
                </div>
                <div className="pt-4">
                  <button type="submit" disabled={submitting} className="pill-button disabled:opacity-50" data-testid="contact-submit-button">
                    {submitting ? "Sending…" : "Submit"}
                  </button>
                </div>
              </form>
            </Reveal>
          )}

          {/* Direct contact */}
          <Reveal className="mt-20 border-t border-white/20 pt-10" delay={100}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
              <a
                href={`mailto:${content.contact.email}`}
                className="font-comfortaa text-sm text-white/90 hover:text-white flex items-center gap-2.5"
                style={{ transition: "color 0.25s ease" }}
                data-testid="contact-email-link"
              >
                <Mail size={17} strokeWidth={1.5} /> {content.contact.email}
              </a>
              <a
                href={content.contact.calendly}
                target="_blank"
                rel="noopener noreferrer"
                className="font-comfortaa text-sm text-white/90 hover:text-white flex items-center gap-2.5"
                style={{ transition: "color 0.25s ease" }}
                data-testid="contact-calendly-link"
              >
                <CalendarClock size={17} strokeWidth={1.5} /> Book a 30-min call
              </a>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8" data-testid="contact-socials">
              {content.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.name];
                if (!Icon) return null;
                return (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    className="text-white/70 hover:text-white"
                    style={{ transition: "color 0.25s ease" }}
                    data-testid={`contact-social-${s.name.toLowerCase()}`}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                  </a>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
